import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useVoiceFixture } from "@/hooks/useVoiceFixture";
import { useNextFixture } from "@/hooks/useNextFixture";
import { FixtureBanner } from "@/components/FixtureBanner";
import { ScorersCard } from "@/components/ScorersCard";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, LineChart, Line, YAxis } from "recharts";
import { api } from "@/lib/apiClient";
import { LeagueSelect } from "@/components/LeagueSelect";
import { TeamSelect } from "@/components/TeamSelect";
import { PredictionCard } from "@/components/PredictionCard";
import { MatchupLineups } from "@/components/MatchupLineups";
import { OctonStats } from "@/components/OctonStats";
import { useAva } from "@/context/AvaContext";
import { PitchView } from "@/components/PitchView";
import { Swords, Volume2, Zap, Loader2, ShieldAlert, HeartPulse } from "lucide-react";
import { toast } from "sonner";

const selectCls = "rounded-xl border border-white/10 bg-[#0a0a0a] px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/50 max-w-[220px]";

export default function FormationAdvisor() {
  const { speak } = useAva();
  const loc = useLocation();
  const preset = loc.state || {};
  const [leagues, setLeagues] = useState([]);
  const [league, setLeague] = useState(preset.league || 39);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState(preset.team_id || null);
  const [oppId, setOppId] = useState(preset.opponent_id || null);
  const [venue, setVenue] = useState("home");
  const { fixture: nextFixture, markTouched } = useNextFixture(teamId, oppId, setVenue);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trend, setTrend] = useState(null);
  const trendPoll = useRef(null);
  const lastRunPair = useRef("");
  const runSeq = useRef(0);

  useEffect(() => { api.get("/leagues").then((r) => setLeagues(r.data)); }, []);
  useEffect(() => {
    let live = true;
    api.get(`/teams?league=${league}`).then((r) => {
      if (!live) return;
      setTeams(r.data);
      if (preset.team_id || preset.opponent_id) {
        if (!teamId) setTeamId(preset.team_id || r.data[0]?.id);
        if (!oppId) setOppId(preset.opponent_id || r.data[1]?.id);
        return;
      }
      // A voice fixture is being resolved — hold, don't default to the wrong match.
      if (preset.pendingFixture) return;
      // stay ahead of the game: preselect the NEXT upcoming fixture in this league
      api.get(`/fixtures?league=${league}&n=1`).then((fr) => {
        const fx = fr.data?.[0];
        if (fx) { setTeamId(fx.home.id); setOppId(fx.away.id); }
        else { setTeamId(r.data[0]?.id); setOppId(r.data[1]?.id); }
      }).catch(() => { setTeamId(r.data[0]?.id); setOppId(r.data[1]?.id); });
    });
    return () => { live = false; };
  }, [league]);
  // The MOMENT a fixture is set (voice OR manual pick), auto-run the formation analysis so ALL cards —
  // line-ups, prediction and formation — compute in PARALLEL, not one after another. Debounced so rapid
  // dropdown changes don't fire multiple runs; de-duped per fixture so it won't re-run needlessly.
  useEffect(() => {
    if (!(teamId && oppId && Number(teamId) !== Number(oppId))) return;
    const pair = `${teamId}-${oppId}-${venue}`;
    if (lastRunPair.current === pair) return;
    const t = setTimeout(() => { lastRunPair.current = pair; run(); }, 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [teamId, oppId, venue]);
  // React to a NEW fixture arriving via voice navigation after the page already mounted (instant route change,
  // then the resolved clubs land a moment later). A pending fixture CLEARS the old one so stale cards never show.
  useEffect(() => {
    const p = loc.state || {};
    if (p.pendingFixture) { runSeq.current += 1; setTeamId(null); setOppId(null); setResult(null); setLoading(false); lastRunPair.current = ""; return; }
    if (p.league) setLeague(p.league);
    if (p.venue) setVenue(p.venue);
    if (p.team_id) setTeamId(p.team_id);
    if (p.opponent_id) setOppId(p.opponent_id);
    /* eslint-disable-next-line */
  }, [loc.key]);

  // live confidence-trend: polls while the deep dive keeps learning
  useEffect(() => {
    clearInterval(trendPoll.current);
    if (!result?.recommended || !teamId) { setTrend(null); return; }
    const f = result.recommended.formation;
    const load = () => api.get(`/advisor/trend?team_id=${Number(teamId)}&formation=${encodeURIComponent(f)}`)
      .then((r) => setTrend(r.data)).catch(() => {});
    load();
    trendPoll.current = setInterval(load, 6000);
    return () => clearInterval(trendPoll.current);
  }, [result, teamId]);

  useVoiceFixture({ onRerun: () => { lastRunPair.current = ""; run(); } });

  const run = async () => {
    if (!teamId || !oppId || teamId === oppId) { toast.error("Pick two different clubs"); return; }
    const seq = ++runSeq.current;
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/advisor/formation", { team_id: Number(teamId), opponent_id: Number(oppId) });
      if (seq !== runSeq.current) return; // fixture changed while computing — drop the stale result
      setResult(r.data);
      speak(r.data.advice);
    } catch (e) {
      if (seq === runSeq.current) toast.error("OCTON could not compute advice");
    } finally { if (seq === runSeq.current) setLoading(false); }
  };

  const rec = result?.recommended;
  const radar = rec ? [
    { k: "Attack", v: rec.attack }, { k: "Defense", v: rec.defense },
    { k: "Control", v: rec.control }, { k: "Shape belief %", v: rec.win_prob },
  ] : [];

  return (
    <div className="px-6 pb-24 pt-20 sm:px-10 lg:px-16" data-octon-fixture={teamId && oppId ? `${Number(teamId)}-${Number(oppId)}` : ""} data-octon-fixture-label={teamId && oppId ? `${teams.find((t) => t.id === Number(teamId))?.name || ""} vs ${teams.find((t) => t.id === Number(oppId))?.name || ""}` : ""}>
      <p className="font-tac text-[11px] tracking-[0.35em] txt-cyan">MATCH INTELLIGENCE · REAL SQUADS</p>
      <h1 className="font-head mt-2 text-5xl font-black uppercase tracking-tighter">Formation Advisor</h1>
      <p className="mt-2 max-w-xl text-sm text-white/50">
        OCTON runs your real squad (minus injuries) through its Bayesian formation belief — now learned from each club's <span className="text-white">own historical record</span> — and picks the winning shape &amp; player mix.
      </p>

      <div className="mt-6 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="font-tac text-[9px] tracking-widest text-white/40">LEAGUE</label>
          <LeagueSelect testId="league-select" value={league} onChange={(id) => setLeague(id)} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="font-tac text-[9px] tracking-widest text-white/40">YOUR CLUB</label>
          <TeamSelect testId="team-select" teams={teams} value={teamId} onChange={(id) => setTeamId(id)} placeholder="Your club" />
        </div>
        <Swords className="mb-3 text-white/30" size={18} />
        <div className="flex flex-col gap-1">
          <label className="font-tac text-[9px] tracking-widest text-white/40">OPPONENT</label>
          <TeamSelect testId="opponent-select" teams={teams} value={oppId} onChange={(id) => setOppId(id)} placeholder="Opponent" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="font-tac text-[9px] tracking-widest text-white/40">VENUE</label>
          <select data-testid="advisor-venue" value={venue} onChange={(e) => { markTouched(); setVenue(e.target.value); }}
            className="rounded-xl border border-white/10 bg-[#0a0a0a] px-3 py-3 text-sm text-white outline-none focus:border-cyan-400/50">
            <option value="home">Home</option><option value="away">Away</option><option value="neutral">Neutral</option>
          </select>
        </div>
        <button data-testid="run-advisor-btn" onClick={run} disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl bg-cyan-400 px-6 py-3 text-sm font-semibold text-black transition hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] disabled:opacity-40">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />} {loading ? "Computing…" : "Advise Formation"}
        </button>
      </div>

      {loc.state?.pendingFixture && (!teamId || !oppId) && (
        <div className="card-tac mt-6 flex items-center gap-2 p-5 text-sm text-white/60" data-testid="fixture-loading">
          <Loader2 size={16} className="animate-spin txt-cyan" /> Opening the fixture OCTON asked for…
        </div>
      )}
      {loc.state?.fixtureError && (
        <div className="mt-6 flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-300" data-testid="fixture-error">
          <ShieldAlert size={15} /> OCTON couldn't find a club called "{loc.state.fixtureError}" — pick the clubs above or say the name again.
        </div>
      )}

      {teamId && oppId && <FixtureBanner fixture={nextFixture} teamName={teams.find((t) => t.id === Number(teamId))?.name} />}

      <MatchupLineups teamId={teamId} oppId={oppId} />

      {teamId && oppId && Number(teamId) !== Number(oppId) && nextFixture !== undefined && (
        <>
          <PredictionCard
            teamId={teamId} oppId={oppId} venue={venue} autoRun venueSource={nextFixture ? "real fixture" : "your selection"}
            teamName={teams.find((t) => t.id === Number(teamId))?.name}
            oppName={teams.find((t) => t.id === Number(oppId))?.name}
          />
          <ScorersCard teamId={teamId} oppId={oppId} venue={venue} />
          <OctonStats teamId={teamId} oppId={oppId} />
        </>
      )}

      {loading && !result && (
        <div className="card-tac mt-8 flex items-center gap-2 p-5 text-sm text-white/60" data-testid="formation-loading"
          data-octon-section="formation recommended xi shape advice analysis briefing alternative shapes" data-octon-label="Formation Analysis" data-octon-state="loading">
          <Loader2 size={16} className="animate-spin txt-cyan" /> OCTON is computing the formation analysis…
        </div>
      )}

      {result && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-8 grid gap-4 lg:grid-cols-3"
          data-octon-section="formation recommended xi shape advice analysis" data-octon-label="Formation Analysis" data-octon-state="ready">
          <div className="card-tac p-4" data-octon-section="recommended xi starting eleven pitch" data-octon-label="Recommended XI">
            <div className="mb-2 flex items-center gap-2">
              <img src={result.team.logo} alt="" className="h-6 w-6 object-contain" />
              <span className="font-tac text-[10px] tracking-widest text-white/50">RECOMMENDED XI</span>
            </div>
            <PitchView lineup={result.lineup} teamId={teamId} />
          </div>

          <div className="space-y-4 lg:col-span-2">
            <div className="card-tac p-6" data-testid="recommendation-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="mb-1 flex items-center gap-2 text-sm text-white/50">
                    <img src={result.team.logo} className="h-5 w-5 object-contain" alt="" /> {result.team.name}
                    <span className="text-white/30">vs</span>
                    <img src={result.opponent.logo} className="h-5 w-5 object-contain" alt="" /> {result.opponent.name}
                  </div>
                  <h2 className="font-head text-6xl font-black tracking-tighter txt-cyan">{rec.formation}</h2>
                  <p className="text-sm text-white/60">{rec.style} · squad rating {result.team_quality} vs {result.opp_quality}</p>
                  {result.actual_shape?.base && (
                    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="actual-shape">
                      <span className="rounded-full border border-[#ccff00]/30 bg-[#ccff00]/[0.07] px-2.5 py-1 font-tac text-[9px] tracking-widest" style={{ color: "#ccff00" }}>
                        REAL BASE SHAPE · {result.actual_shape.base}
                        {result.actual_shape.tally?.[result.actual_shape.base] ? ` ×${result.actual_shape.tally[result.actual_shape.base]}` : ""}
                        {result.actual_shape.matches ? ` / last ${result.actual_shape.matches}` : ""}
                      </span>
                      {result.actual_shape.last_match && (
                        <span className="rounded-full border border-white/10 px-2.5 py-1 font-tac text-[9px] tracking-widest text-white/50">
                          LAST MATCH · {result.actual_shape.last_match}
                        </span>
                      )}
                      <span className="font-tac text-[9px] tracking-widest" style={{ color: rec.is_actual ? "#33d17a" : "#ffb800" }}>
                        {rec.is_actual ? "✓ MATCHES WHAT THEY PLAY" : "⤴ TACTICAL DEVIATION — see reasoning"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-head text-5xl font-black tracking-tighter" style={{ color: "#ccff00" }}>{rec.win_prob}%</p>
                  <p className="font-tac text-[9px] tracking-widest text-white/40" title="OCTON's Bayesian confidence in this SHAPE for this squad — not the match win probability (see Match Prediction)">SHAPE BELIEF · NOT MATCH WIN %</p>
                  <p className="mt-1 text-[11px] text-white/40">±{rec.uncertainty}% · <span className="text-cyan-300">{rec.team_samples} {result.team.name.split(" ")[0]} matches</span> · {rec.league_samples} league · <span style={{ color: rec.xi_morale >= 68 ? "#33d17a" : rec.xi_morale >= 52 ? "#00e5ff" : "#ffb800" }}>morale {rec.xi_morale}%</span></p>
                </div>
              </div>

              {trend?.points?.length > 1 && (
                <div className="mt-4 rounded-xl border border-white/8 bg-black/30 p-3" data-testid="confidence-trend">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="font-tac text-[9px] tracking-widest text-white/40">
                      CONFIDENCE TREND · {trend.formation}
                    </span>
                    <span className="flex items-center gap-1.5 text-[10px] text-cyan-300">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#ccff00]" />
                      {trend.current.wp}% · {trend.team_samples} matches learned
                    </span>
                  </div>
                  <div className="h-16">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trend.points.map((p, i) => ({ i, wp: p.wp }))}>
                        <YAxis domain={[0, 100]} hide />
                        <Line type="monotone" dataKey="wp" stroke="#ccff00" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="mt-1 text-[10px] text-white/35">
                    Sharpening live as OCTON reviews {result.team.name}'s history — reopen or wait for the next update.
                  </p>
                </div>
              )}

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radar}>
                      <PolarGrid stroke="#222" />
                      <PolarAngleAxis dataKey="k" tick={{ fill: "#888", fontSize: 10 }} />
                      <Radar dataKey="v" stroke="#00e5ff" fill="#00e5ff" fillOpacity={0.25} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-4" data-octon-section="briefing octon advice summary" data-octon-label="OCTON Briefing">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-tac text-[9px] tracking-widest text-cyan-300">OCTON BRIEFING</span>
                    <button data-testid="speak-advice-btn" onClick={() => speak(result.advice, { force: true })} className="text-cyan-300 hover:text-white"><Volume2 size={15} /></button>
                  </div>
                  <p className="text-sm leading-relaxed text-white/80" data-testid="advice-text">{result.advice}</p>
                  {result.analysis?.mindset_note && (
                    <div className="mt-3 flex items-start gap-2 rounded-xl border border-white/8 bg-white/[0.03] p-2.5" data-testid="mindset-note">
                      <HeartPulse size={14} className="mt-0.5 shrink-0" style={{ color: "#ff6ad5" }} />
                      <p className="text-[12px] leading-snug text-white/70"><span className="text-white/50">Mindset — </span>{result.analysis.mindset_note}</p>
                    </div>
                  )}
                  {result.codex_applied?.length > 0 && (
                    <div className="mt-3 border-t border-white/5 pt-2" data-testid="codex-applied">
                      <p className="mb-1.5 font-tac text-[8px] tracking-widest text-white/35">TACTICAL THEORY APPLIED</p>
                      <div className="flex flex-wrap gap-1.5">
                        {result.codex_applied.map((c) => (
                          <span key={c.title} className="rounded-full border border-cyan-400/20 bg-cyan-400/5 px-2.5 py-1 text-[10px] text-cyan-200">{c.title}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {result.injured?.length > 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-2 text-xs text-red-300" data-testid="injured-list">
                  <ShieldAlert size={14} /> Unavailable: {result.injured.map((p) => `${p.name} (${p.reason})`).join(" · ")}
                </div>
              )}
            </div>

            <div className="card-tac p-5" data-octon-section="alternative shapes formations options" data-octon-label="Alternative Shapes">
              <p className="font-tac text-[10px] tracking-widest text-white/40 mb-3">ALTERNATIVE SHAPES</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {result.alternatives.map((a) => (
                  <div key={a.formation} className="rounded-xl border border-white/8 p-3">
                    <p className="font-head text-2xl font-bold tracking-tight">{a.formation}</p>
                    <p className="text-[11px] text-white/40">{a.style}</p>
                    <p className="mt-1 font-mono text-sm" style={{ color: "#ccff00" }}>{a.win_prob}% <span className="text-[9px] text-white/35">shape belief</span></p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
